/*
  <clearance-grid-fill> — keeps the variant-clearance product grid full.

  Shopify paginates a collection by product (sections/main-collection-product-grid.liquid,
  `paginate collection.products by products_per_page`, max 50 per page). In variant clearance mode
  each product renders 0..n cards (one per discounted variant that matches the active
  Strength/Ratio/Size filters), so a product page can come back with only a handful of cards and the
  numbered paginator describes product pages, not the cards the customer sees.

  This element wraps `#product-grid`. On connect (initial render and every facets.js re-render, which
  replaces #ProductGridContainer and so re-creates this element) it:
    1. hides the product-page paginator (kept when the visitor deep-linked to a later page, so the
       earlier pages stay reachable);
    2. fetches the following product pages through the Section Rendering API and appends their
       cards until at least `data-cards-per-page` cards are on screen or the pages run out;
    3. shows a "Load more" control that continues from the next product page;
    4. once every product page has been consumed from page 1, reports the true card count in the
       product-count header and shows the clearance empty state when nothing matched.
*/

if (!customElements.get('clearance-grid-fill')) {
  const PAGES_PER_FETCH_BATCH = 3;
  const MAX_FETCH_BATCHES_PER_RUN = 4;
  const MORE_LABEL = 'Load more clearance options';

  class ClearanceGridFill extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('#product-grid');
      if (!this.grid) return;

      this.sectionId = this.dataset.sectionId;
      this.totalPages = parseInt(this.dataset.pages, 10) || 1;
      this.startPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.nextPage = this.startPage + 1;
      this.cardsPerPage = parseInt(this.dataset.cardsPerPage, 10) || 16;
      this.running = false;

      this.footer = this.querySelector('[data-clearance-grid-fill-footer]');
      this.status = this.querySelector('[data-clearance-grid-fill-status]');
      this.moreButton = this.querySelector('[data-clearance-grid-fill-more]');
      this.emptyState = this.querySelector('[data-clearance-grid-fill-empty]');
      this.paginator = this.querySelector('.pagination-wrapper');

      if (this.totalPages <= 1) return;

      if (this.paginator && this.startPage === 1) this.paginator.hidden = true;
      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => this.fillTo(this.cardCount() + this.cardsPerPage));
      }

      this.fillTo(this.cardsPerPage);
    }

    cardCount() {
      return this.grid.children.length;
    }

    hasMorePages() {
      return this.nextPage <= this.totalPages;
    }

    pageUrl(page) {
      // Shopify accepts comma-separated same-facet values, but treats an encoded comma (`%2C`) as
      // part of one value. Preserve the browser's raw query instead of reserializing it through
      // URLSearchParams, while replacing only the pagination/section parameters we own.
      const params = window.location.search
        .slice(1)
        .split('&')
        .filter(Boolean)
        .filter((param) => !['page', 'section_id'].includes(param.split('=', 1)[0]));
      params.push(`page=${encodeURIComponent(String(page))}`);
      params.push(`section_id=${encodeURIComponent(this.sectionId)}`);
      return `${window.location.pathname}?${params.join('&')}`;
    }

    async fillTo(targetCards) {
      if (this.running) return;
      this.running = true;
      this.setLoading(true);

      let batches = 0;
      try {
        while (
          this.isConnected &&
          this.cardCount() < targetCards &&
          this.hasMorePages() &&
          batches < MAX_FETCH_BATCHES_PER_RUN
        ) {
          batches += 1;
          const lastPage = Math.min(this.nextPage + PAGES_PER_FETCH_BATCH - 1, this.totalPages);
          const pages = [];
          for (let page = this.nextPage; page <= lastPage; page += 1) pages.push(page);

          const responses = await Promise.all(pages.map((page) => fetch(this.pageUrl(page))));
          if (!this.isConnected) return;
          for (const response of responses) {
            if (!response.ok) throw new Error(`Section render failed (${response.status}) for ${response.url}`);
            this.appendCards(await response.text());
            this.nextPage += 1;
          }
        }
      } catch (error) {
        console.error('clearance-grid-fill: could not load the next product page', error);
      } finally {
        this.running = false;
        if (this.isConnected) {
          this.setLoading(false);
          this.render();
        }
      }
    }

    appendCards(html) {
      const fetched = new DOMParser().parseFromString(html, 'text/html');
      fetched.querySelectorAll('#product-grid > li').forEach((item) => {
        // Match facets.js: fetched cards skip the reveal-on-scroll animation.
        if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
        item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
        this.grid.appendChild(item);
      });
    }

    setLoading(isLoading) {
      this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (!this.moreButton) return;
      this.moreButton.disabled = isLoading;
      this.moreButton.textContent = isLoading ? 'Loading…' : MORE_LABEL;
    }

    render() {
      const count = this.cardCount();
      const label = count === 1 ? 'clearance option' : 'clearance options';
      const complete = !this.hasMorePages() && this.startPage === 1;

      if (this.footer) this.footer.hidden = complete && count === 0;
      if (this.moreButton) this.moreButton.hidden = !this.hasMorePages();
      if (this.emptyState) this.emptyState.hidden = !(complete && count === 0);
      if (this.status) {
        this.status.textContent = complete ? `All ${count} ${label} shown` : `Showing ${count} ${label}`;
        if (complete && count === 0) this.status.textContent = '';
      }
      if (complete) this.renderProductCount(`${count} ${label} found`);
    }

    renderProductCount(text) {
      ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
      });
    }
  }

  customElements.define('clearance-grid-fill', ClearanceGridFill);
}
